/**
 * Parses a prediction job's stored `config_json` into the input components shown as chips on the
 * Predictions, batch, and prediction detail pages.
 *
 * Mirrors the canonical schema-v1 shapes in
 * benchling/model_hub/scientific_models/config/prediction_config_v1.py. A raw component
 * (`_type: 'aminoAcids'` / `'smiles'`) carries an optional human-readable `name` and its
 * sequence/SMILES `value`; a registry-entity component (`aaSequenceId` / `proteinId` / `moleculeId`)
 * carries only the entity's `id`, so its chip resolves the name itself.
 *
 * The parser is defensive: missing or legacy configs return an empty array so those rows simply
 * render no chips rather than throwing.
 */

export type PredictionInputType = 'aminoAcids' | 'smiles';

export interface RawPredictionInput {
  type: PredictionInputType;
  name?: string;
  value?: string;
}

export interface EntityPredictionInput {
  type: 'entity';
  entityId: string;
}

export type PredictionInput = RawPredictionInput | EntityPredictionInput;

const ENTITY_ID_COMPONENT_TYPES: ReadonlySet<unknown> = new Set(['aaSequenceId', 'proteinId', 'moleculeId']);

function parseComponent(component: unknown): PredictionInput | null {
  if (component == null || typeof component !== 'object' || Array.isArray(component)) {
    return null;
  }
  const record = component as Record<string, unknown>;
  const type = record._type;
  if (ENTITY_ID_COMPONENT_TYPES.has(type)) {
    return typeof record.id === 'string' && record.id.length > 0
      ? {type: 'entity', entityId: record.id}
      : null;
  }
  if (type !== 'aminoAcids' && type !== 'smiles') {
    return null;
  }
  const name = typeof record.name === 'string' && record.name.length > 0 ? record.name : undefined;
  const value = typeof record.value === 'string' && record.value.length > 0 ? record.value : undefined;
  return {
    type,
    ...(name != null ? {name} : {}),
    ...(value != null ? {value} : {}),
  };
}

export function parsePredictionInputs(
  configJson: Record<string, unknown> | null | undefined
): Array<PredictionInput> {
  if (configJson == null) {
    return [];
  }

  // Multimer shape is a `components` array; generative models list theirs under `targets`.
  const components = Array.isArray(configJson.components) ? configJson.components : configJson.targets;
  if (Array.isArray(components)) {
    return components
      .map((component) => parseComponent(component))
      .filter((input): input is PredictionInput => input != null);
  }

  // Monomer shape: a single `component` object.
  const single = parseComponent(configJson.component);
  return single != null ? [single] : [];
}
